import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx, focusRing } from './cx';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, hint, className, disabled, ...rest },
  ref,
) {
  return (
    <label
      className={cx(
        'flex items-start gap-3 min-h-tap py-2 cursor-pointer select-none',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <input
        ref={ref}
        type="radio"
        disabled={disabled}
        className={cx('mt-0.5 h-[18px] w-[18px] shrink-0 accent-[rgb(var(--ct-green-rgb))]', focusRing)}
        {...rest}
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm text-ink">{label}</span>
        {hint ? <span className="text-xs text-ink-3">{hint}</span> : null}
      </span>
    </label>
  );
});

export interface RadioGroupProps {
  /** Radios need a group label that is not a <label>, hence fieldset/legend. */
  legend: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function RadioGroup({ legend, hint, error, className, children }: RadioGroupProps) {
  return (
    <fieldset className={cx('flex flex-col gap-1', className)}>
      <legend className="text-xs font-semibold text-ink-2">{legend}</legend>
      {hint ? <p className="text-xs text-ink-3">{hint}</p> : null}
      <div className="flex flex-col">{children}</div>
      {error ? (
        <p role="status" className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
